import Button from "components/common/Button";
import CustomCalendar from "components/common/CalendarComp.js/Calendar";
import CustomTable from "components/common/CustomTable";
import Dropdown from "components/common/Dropdown";
import TextInput from "components/common/InputField/TextInput";
import MultiChannelSelect from "components/common/MultiChannelSelect";
import MultipleLineChart from "components/common/MultipleLineChart";
import SynthTooltip from "components/common/SynthTooltip";
import { AnalyticsCard } from "components/views/Ldb/Dashboard/BusinessAnalytics/Card";
import { formatMoney, getDashboardChartData } from "helpers";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import useGetAllWarehouses from "hooks/useGetAllWarehouses";
import { truncate } from "lodash";
import millify from "millify";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import ResponsivePaginationComponent from "react-responsive-pagination";
import { dashboardActions } from "redux/Ldb/actions";

type GraphViewProps = {
    resultMessage: {
        data: Record<string, any>;
        inventories: Record<string, any>;
        filters: Record<string, any>;
        pagination: {
            current: number;
            next: number;
            number_of_pages: number;
            perPage: number;
        };
    };
    toolName: string;
};
const FinishedGoodsForecast = ({ resultMessage, toolName }: GraphViewProps) => {
    const customRef = useRef(null);

    const dispatch = useAppDispatch();
    const [chartData, setChartData] = useState<{ [key: string]: any }>([]);
    const [page, setPage] = useState<{
        currentPage: number;
        noOfPages: number;
    }>({
        currentPage: 1,
        noOfPages: 1,
    });
    const warehouseRef = useRef(null);
    const [warehouse, setWarehouse] = useState<string>("");
    const [channelFilter, setChannelFilter] = useState<string>("");
    const [finishedGoodsForecastData, setFinishedGoodsForecastData] = useState<any[]>([]);
    const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
    const [customValue, customOnChange] = useState("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [date, setDate] = useState<{ startDate: string; endDate: string }>({
        startDate: "",
        endDate: "",
    });

    //graph
    useEffect(() => {
        if (resultMessage?.data && resultMessage?.filters?.isGraph) {
            let chartDataValues;
            chartDataValues = getDashboardChartData(resultMessage?.data, "custom", [
                moment(resultMessage?.filters?.startDate).format("MMM DD YYYY"),
                moment(resultMessage?.filters?.endDate).format("MMM DD YYYY"),
            ]);

            setChartData(chartDataValues);
        }
    }, [resultMessage]);

    // table

    // console.log(resultMessage);

    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();
    const { fetchFinishedGoodsForecastSuccess } = useAppSelector((state) => state.dashboard);

    const {
        data: warehouseData,
        search: warehouseSearch,
        handleSearch: handleWarehouseSearch,
        handleDebouncedChange: handleWarehouseDebouncedChange,
    } = useGetAllWarehouses();

    const handleWarehouseChange = (e) => {
        const { value } = e.target;
        const arrayValue = value !== "" && value?.split("-");
        setWarehouse(value);
    };

    useEffect(() => {
        if (
            (page?.currentPage === 1 && (channelFilter || warehouse)) ||
            channelFilter ||
            warehouse
        ) {
            dispatch(
                dashboardActions?.getFinishedGoodsForecast(
                    page?.currentPage,
                    "",
                    resultMessage?.filters?.inventoryId ?? "",
                    channelFilter ?? resultMessage?.filters?.channels?.join(","),
                    "",
                    "",
                    "",
                    "",
                    warehouse?.split("-")[1]
                )
            );
        }
    }, [dispatch, page?.currentPage, channelFilter, warehouse]);

    useEffect(() => {
        if (
            (resultMessage?.filters?.startDate && resultMessage?.filters?.endDate) ||
            (startDate && endDate)
        ) {
            setDate({
                startDate: startDate || resultMessage?.filters?.startDate,
                endDate: endDate || resultMessage?.filters?.endDate,
            });
        }
    }, [resultMessage?.filters?.startDate, resultMessage?.filters?.endDate, startDate, endDate]);

    useEffect(() => {
        if (Boolean(fetchFinishedGoodsForecastSuccess)) {
            setFinishedGoodsForecastData(fetchFinishedGoodsForecastSuccess?.inventories?.data);
        }
    }, [fetchFinishedGoodsForecastSuccess]);

    const forecastTableHeader = [
        // {
        //     title: <div className="flex space-x-1.5 items-center"></div>,
        //     widthClass: "w-[50px] !border-r-none",
        // },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">Product</span>

                    <SynthTooltip
                        content={
                            "The unique name identifying this specific item in your inventory."
                        }
                        divId={"product-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[180px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Current stock level
                    </span>

                    <SynthTooltip
                        content={
                            "The actual quantity of the product currently available in stock for sale or use at this moment."
                        }
                        divId={"current-stock-level-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[180px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Demand forecast
                    </span>

                    <SynthTooltip
                        content={
                            "A projection of how much of this product you’ll need in the future, based on past sales, market trends, and upcoming promotions."
                        }
                        divId={"demand-forecast-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[150px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Stock deficit
                    </span>

                    <SynthTooltip
                        content={
                            "The number of units your inventory is short based on forecasted demand. This highlights how much stock needs to be replenished to meet future needs."
                        }
                        divId={"stock-deficit-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[150px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Cost forecast
                    </span>

                    <SynthTooltip
                        content={
                            "The predicted total cost to fulfill the forecasted demand, based on the product’s unit cost and the stock deficit."
                        }
                        divId={"cost-forecast-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[150px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Replenishment due
                    </span>

                    <SynthTooltip
                        content={
                            "The quantity of stock replenishment needed to mitigate a stockout scenario."
                        }
                        divId={"replenishment-due-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[180px] !border-r-none",
        },
    ];

    const tableData =
        page?.currentPage === 1 && channelFilter === "" && warehouse === ""
            ? resultMessage?.inventories?.data
            : finishedGoodsForecastData;

    const forecastTableBody = tableData?.map((item, idx) => {
        const warehouseId = warehouse?.split("-")[1];
        const stockDeficit = warehouseId
            ? item?.forecast?.currentStock - item?.forecast?.forecast
            : item?.productStockDetails?.stockLevel - item?.forecast?.forecast;

        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600 break-all py-2`}
                    >
                        <div>
                            <p className="">{item?.productName || "N/A"}</p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney().format(
                                    warehouseId
                                        ? item?.forecast?.currentStock
                                        : item?.productStockDetails?.stockLevel
                                )}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney()?.format(item?.forecast?.forecast)}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={` text-xs ${
                                    String(stockDeficit)[0] === "-" ? "text-r-55" : "text-slate-500"
                                }`}
                            >
                                {String(stockDeficit)[0] === "-" ? (
                                    formatMoney()?.format(String(stockDeficit)?.slice(1)) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                ) : (
                                    <span className="text-green-500">Sufficent</span>
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                ${formatMoney()?.format(item?.forecast?.forecastedCost)}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={` text-xs ${
                                    String(stockDeficit)[0] === "-" ? "text-r-55" : "text-slate-500"
                                }`}
                            >
                                {item?.forecast?.forecastedNeeds === 0 ? (
                                    <span className="text-green-500">Sufficent</span>
                                ) : (
                                    formatMoney()?.format(item?.forecast?.forecastedNeeds) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                )}
                            </p>
                        </div>
                    </div>
                ),
                widthClass: " !border-b",
            },
        ];
    });

    useEffect(() => {
        if (customValue[0] !== customValue[1]) {
            setStartDate(moment(customValue[0])?.format("YYYY-MM-DD"));
            setEndDate(moment(customValue[1])?.format("YYYY-MM-DD"));
        }
    }, [customValue]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customRef.current && !customRef.current.contains(event.target)) {
                setShowCustomDate(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [customRef]);

    return (
        <div>
            {resultMessage?.filters?.isGraph && (
                <div className="border border-slate-200 rounded-xl px-5 shadow-boxShadow-9">
                    <div className=" mt-8">
                        <div className="flex gap-3.5 mb-6">
                            <div className="flex items-center">
                                <img
                                    src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1723568484/blue_line_yds7b9.svg"
                                    alt="this-trend"
                                    className="w-4 h-4"
                                />
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    Current sales
                                </p>
                            </div>

                            <div className="flex items-center">
                                <img
                                    src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1712800595/chartLine2_o41vii.svg"
                                    alt="this-trend"
                                    className="w-4 h-4"
                                />
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    Your configured plan
                                </p>
                            </div>

                            <div className="flex items-center">
                                <img
                                    src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1723568484/red_line_yfjgnt.svg"
                                    alt="this-trend"
                                    className="w-4 h-4"
                                />
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    Synth's predicted forecast
                                </p>
                            </div>

                            <div className="flex items-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 16 15"
                                >
                                    <path
                                        stroke="#6562E5"
                                        strokeDasharray="2 2"
                                        strokeWidth="1.5"
                                        d="M0 8.5h3.5l2.5-6 3.5 10 1.5-4h5"
                                    ></path>
                                </svg>
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    Your saved target{" "}
                                </p>
                            </div>
                        </div>

                        <div className="flex max-lg:flex-col justify-end mb-8">
                            <div className="flex w-full lg:w-fit max-lg:items-center max-lg:justify-between max-lg:border-b max-lg:border-b-slate-200">
                                <AnalyticsCard
                                    title={"Forecasted revenue"}
                                    figure={
                                        resultMessage?.data?.forecastTotalSale !== undefined
                                            ? millify(
                                                  Number(resultMessage?.data?.forecastTotalSale),
                                                  {
                                                      precision: 2,
                                                  }
                                              )
                                            : "----"
                                    }
                                    currency={
                                        Boolean(resultMessage?.data?.forecastTotalSale) && "$"
                                    }
                                    customFigure={
                                        Boolean(resultMessage?.data?.configuredRevForecast) &&
                                        resultMessage?.data?.configuredRevForecast !== undefined
                                            ? millify(
                                                  Number(
                                                      resultMessage?.data?.configuredRevForecast
                                                  ),
                                                  {
                                                      precision: 2,
                                                  }
                                              )
                                            : "----"
                                    }
                                    loader={false}
                                    iconTwo
                                    hideDirection={true}
                                    hasCustomValue={true}
                                    increaseClassName="!text-g-60"
                                    innerClassName="!justify-end"
                                    addTextTwoClass="!whitespace-nowrap"
                                    addTextTwo={"Synth's revenue forecast"}
                                    customSubTextClass="!whitespace-nowrap"
                                    customSubText={"Configured revenue forecast"}
                                    className="!shadow-none !border-y-0 !border-l-0 lg:!border-r lg:!border-r-slate-200 !py-2 !pl-0 lg:!pl-[15px] !pr-8  !rounded-none flex-1 lg:!w-auto"
                                    arrUpClass={"!text-g-60"}
                                    titleClassName="!text-slate-600 !text-[10px]"
                                    figureClassName="!mt-2.5 !text-slate-600  !text-[20px] !leading-[28px]"
                                    tooltipClassName="!max-w-[195px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-l-transparent before:!border-r-gm-75 before:!top-[40%] before:!-left-[9%] !z-[999] before:!border-[6px] !left-[50px] xs:!left-[120px]"
                                    toolTipContent={
                                        <div className="!p-3">
                                            <p className="underline text-xs font-rocGroteskMedium mb-1">
                                                Forecasted revenue
                                            </p>
                                            <p className="text-[11px]">
                                                This is the estimated amount of money that you are
                                                likely to earn over a future period. This is
                                                calculated using external factors such as
                                                seasonality, social media, supplier lead time, and
                                                historical sales.
                                            </p>
                                        </div>
                                    }
                                    addTextTwoTooltipContent={
                                        "This is the total amount our system has forecasted you would sell for all your inventory over the next 6 months. We use the current unit inventory value/amount multiplied by the forecasted quantity."
                                    }
                                    customSubTextTooltipContent={
                                        "This is the total forecasted revenue for all your inventory that you have configured over the next 6 months. We use the current unit inventory value/amount multiplied by the forecasted quantity you set."
                                    }
                                />

                                <div className="h-[29px] lg:hidden w-[1px] bg-slate-200"></div>

                                <AnalyticsCard
                                    title={"Forecasted sales quantity"}
                                    value={Math.round(resultMessage?.data?.totalForecastQty)}
                                    customFigure={
                                        resultMessage?.data?.configuredDemandForecast
                                            ? formatMoney().format(
                                                  resultMessage?.data?.configuredDemandForecast
                                              )
                                            : "----"
                                    }
                                    loader={false}
                                    iconTwo
                                    hasCustomValue={true}
                                    className="!shadow-none !border-y-0 !border-l-0 lg:!border-r lg:!border-r-slate-200 !py-2 !pl-[15px] !pr-8  !rounded-none  flex-1 lg:!w-fit"
                                    titleClassName="!text-slate-500 !text-[10px]"
                                    innerClassName="!justify-end"
                                    addTextTwoClass="!whitespace-nowrap"
                                    addTextTwo={"Synth's demand forecast"}
                                    customSubTextClass="!whitespace-nowrap"
                                    customSubText={"Configured demand forecast"}
                                    figureClassName="!mt-2.5 !text-slate-600  !text-[20px] !leading-[28px]"
                                    tooltipClassName="!max-w-[195px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-r-transparent lg:before:!border-l-transparent before:!border-l-gm-75 lg:before:!border-r-gm-75 before:!top-[40%] before:!-right-[95px] lg:before:!right-auto lg:before:!-left-[9%] !z-[999] before:!border-[6px] !-left-[100px] lg:!left-[120px]"
                                    toolTipContent={
                                        <div className="!p-3">
                                            <p className="underline text-xs font-rocGroteskMedium mb-1">
                                                Forecasted sales quantity
                                            </p>
                                            <p className="text-[11px]">
                                                This is the estimated number of units of all
                                                products that you are likely to sell in a future
                                                period.
                                            </p>
                                        </div>
                                    }
                                    addTextTwoTooltipContent={
                                        "This is the total number of units across all your inventory that you are most likely going to sell over the next 6 months."
                                    }
                                    customSubTextTooltipContent={
                                        "This is the total quantity across all your inventory that you have setup as your likely forecasted units to be sold."
                                    }
                                />
                            </div>

                            <AnalyticsCard
                                title={"Forecasted inventory deficit"}
                                value={Math.round(resultMessage?.data?.forecastedStockNeeded)}
                                loader={false}
                                iconTwo
                                addTextTwo={"(oos)"}
                                addTextTwoClass="!text-r-55 !text-xl"
                                textAndSubtextContainer={"!justify-end"}
                                className="!shadow-none !border-none !py-2 !pl-0 lg:!pl-[15px] !pr-8  !rounded-none !w-auto"
                                titleClassName="!text-slate-600 !text-[10px]"
                                figureClassName="!mt-2.5 !text-r-55  !text-[20px] !leading-[28px] flex gap-1 items-end"
                                tooltipClassName="!max-w-[195px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-l-transparent lg:before:!border-r-transparent before:!border-r-gm-75 lg:before:!border-l-gm-75 before:!top-[40%] before:!-left-[9%] lg:before:!left-auto lg:before:!-right-[6px] !z-[999] before:!border-[6px] !left-[0px] xs:!left-[120px] lg:!-left-[250px]"
                                toolTipContent={
                                    <div className="!p-3">
                                        <p className="underline text-xs font-rocGroteskMedium mb-1">
                                            Forecasted inventory deficit
                                        </p>
                                        <p className="text-[11px] ">
                                            This is the estimated number of units of all your
                                            products that are likely to be out of stock in a future
                                            period. These are the likely out of stock units of
                                            products with respect to the demand forecast.
                                        </p>
                                    </div>
                                }
                                addTextTwoTooltipContent={
                                    "This is the total number of units you would need to meet up the forecasted sales quantity over the next 6 months, based on your current inventory level today."
                                }
                            />
                        </div>
                    </div>

                    <MultipleLineChart mapData={chartData} />
                </div>
            )}

            {!resultMessage?.filters?.isGraph && resultMessage?.inventories?.data?.length > 0 && (
                <div className="overflow-x-clip">
                    <div className="relative border border-slate-200 rounded-xl  mt-[16px]">
                        <div className="lg:flex justify-between items-center px-5 py-[15px]">
                            <div className="flex items-center space-x-6 ">
                                <p className="text-g-75 font-rocGroteskMedium text-sm lg:text-lg mb-4 lg:mb-0">
                                    Finished Goods Forecast
                                </p>
                            </div>

                            <div className="flex items-center space-x-4 ">
                                <div className="">
                                    {" "}
                                    <Button
                                        btnText={`${moment(date?.startDate).format(
                                            "MMM, DD YYYY"
                                        )} - ${moment(date?.endDate).format("MMM, DD YYYY")}`}
                                        btnClassname={
                                            "!w-fit !py-2 flex items-center justify-between h-[36px] rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100 !bg-transparent "
                                        }
                                        onClick={() => setShowCustomDate(true)}
                                    />
                                    <div className="relative">
                                        {showCustomDate && (
                                            <div
                                                ref={customRef}
                                                className="w-[630px] z-[10000] absolute  right-0 top-0 px-[28px] pt-8 pb-8 bg-white
                                                    rounded-2xl shadow-[0px_0px_4px_rgba(0,0,0,0.04),0px_4px_32px_rgba(0,0,0,0.06)]"
                                            >
                                                <CustomCalendar
                                                    showDoubleView={true}
                                                    selectRange={true}
                                                    value={customValue}
                                                    onChange={customOnChange}
                                                    minDate={moment(
                                                        resultMessage?.filters?.startDate
                                                    ).toDate()}
                                                    maxDate={moment(
                                                        resultMessage?.filters?.endDate
                                                    ).toDate()}
                                                />
                                                <div className="flex justify-end">
                                                    <Button
                                                        btnText={"Close"}
                                                        btnClassname={"!w-fit !py-2"}
                                                        onClick={() => setShowCustomDate(false)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="">
                                    <Dropdown
                                        handleChange={(name, value) =>
                                            handleWarehouseChange({
                                                target: { name, value },
                                            })
                                        }
                                        search={
                                            <div ref={warehouseRef} className="">
                                                <TextInput
                                                    value={warehouseSearch}
                                                    name={"search"}
                                                    type={"text"}
                                                    required={false}
                                                    onChange={handleWarehouseSearch}
                                                    onInput={handleWarehouseDebouncedChange}
                                                    inputPlaceholder="Search warehouse"
                                                    containerClassname=""
                                                    inputContainerClassname={"!h-[40px] "}
                                                />
                                            </div>
                                        }
                                        dropdown={
                                            <div className="flex items-center justify-between lg:rounded-[5px] lg:shadow-buttonShadow2 py-[6px] px-[10px] gap-2 !border !border-slate-100">
                                                <div
                                                    className={`text-slate-800 font-rocGroteskMedium text-[13px] inline-block whitespace-nowrap capitalize`}
                                                >
                                                    {warehouse
                                                        ? truncate(warehouse?.split("-")[0], {
                                                              length: 12,
                                                          })
                                                        : "All warehouses"}
                                                </div>
                                                <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                                            </div>
                                        }
                                        dropdownItemsClasses="!px-0 !py-0"
                                        dropdownClassName="!mb-10 !-mt-7"
                                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[5px] !max-h-[200px]
                                         !w-[200px] !min-w-min !pt-0
                            `}
                                        name="valueRange"
                                        dropdownOptions={[
                                            {
                                                label: (
                                                    <div
                                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                                    >
                                                        <i
                                                            className={`${
                                                                warehouse === ""
                                                                    ? "ri-checkbox-line text-carribean-green"
                                                                    : "ri-checkbox-blank-line text-slate-200"
                                                            }    text-xl`}
                                                        ></i>
                                                        All Warehouses
                                                    </div>
                                                ),
                                                value: "",
                                            },
                                            ...warehouseData?.map((item, idx) => ({
                                                label: (
                                                    <div
                                                        key={idx}
                                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                    border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                                    >
                                                        <i
                                                            className={`${
                                                                warehouse?.split("-")[1] ===
                                                                item?._id
                                                                    ? "ri-checkbox-line text-carribean-green"
                                                                    : "ri-checkbox-blank-line text-slate-200"
                                                            }    text-xl`}
                                                        ></i>
                                                        {item?.warehouseName}
                                                    </div>
                                                ),
                                                value: item?.warehouseName + "-" + item?._id,
                                            })),
                                        ]}
                                    />
                                </div>

                                <div className="flex sm:space-x-2.5  max-sm:justify-between max-sm: items-end border-b border-b-slate-200 sm:border-b-0">
                                    <MultiChannelSelect
                                        salesChannel={
                                            salesChannel ||
                                            resultMessage?.filters?.channels?.join(",")
                                        }
                                        value={channelFilter}
                                        onChange={(value) => setChannelFilter(value)}
                                        isLoading={salesChannelLoading}
                                        dropdownContainerClasses=" !right-0"
                                        dropdownClassName="!shadow-buttonShadow2 !h-auto !border-slate-100"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="border-t border-t-slate-200 pt-5 px-5">
                            <CustomTable
                                tableBody={forecastTableBody}
                                tableHeader={forecastTableHeader}
                                isCellBordered={true}
                                stickyHeader={true}
                                headerContainerClass="!bg-slate-50 "
                                headerItemClass="font-rocGroteskMedium "
                                tableClass="mb-4"
                                isScrollable={true}
                                scrollWithin={true}
                                renderEmptyTable={() => null}
                            />

                            <div className="flex justify-end my-5">
                                <ResponsivePaginationComponent
                                    current={page?.currentPage}
                                    total={resultMessage?.pagination?.number_of_pages}
                                    maxWidth={350}
                                    onPageChange={(page) => {
                                        setPage((prev) => ({
                                            ...prev,
                                            currentPage: page,
                                        }));
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinishedGoodsForecast;
